/* eslint-disable no-param-reassign */
import { ExclamationCircleOutlined, FileAddOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Col, Form, Input, message, Row, InputNumber, Slider, Radio, Modal } from 'antd';

import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_BACKGROUND_OPACITY, TEXT_COLOR_BLACK, TEXT_COLOR_WHITE } from '../../../../constants/common';
import { PRODUCT } from '../../../../constants/define/translation';
import { MESSAGE_ERROR } from '../../../../constants/message';

import {
  ProductOrders,
  ProductUpdatable,
  updateProductRepository,
} from '../../../../repository/mytm/shop-product/shopProductRepository';
import { appStyles, defaultImage, formScrolling, selectSelector } from '../../../../styles';
import { Align } from '../../../../types/common';
import { ButtonActionPage, SpinLoading } from '../../../atoms';
import ColorPicker from '../../../molecules/colorpicker/ColorPicker';
import { ConfirmAction } from '../../../molecules/confirm/ConfirmAction';
import { Preview } from '../../../molecules/preview/common';
import { UserContext } from '../../../../repository/context/userContext';
import { postLoginRepository } from '../../../../repository/mytm/auth/loginRepository';

interface FormPromotionProps {
  record?: ProductOrders;
  onRefresh: () => void;
  onCloseModal: () => void;
  setDataRecord: Function;
}

export const FormProduct = (props: FormPromotionProps) => {
  const { t } = useTranslation(PRODUCT);
  const userContext = useContext(UserContext);

  const { record, onRefresh, onCloseModal, setDataRecord } = props;
  const [form] = Form.useForm();
  const [isCreateLoading, setCreateLoading] = useState(false);

  const [textColor, setTextColor] = useState<string | undefined>(record?.textColor || TEXT_COLOR_WHITE);
  const [priceColor, setPriceColor] = useState<string | undefined>(record?.priceColor || TEXT_COLOR_WHITE);
  const [buttonTextColor, setButtonTextColor] = useState<string | undefined>(
    record?.buttonTextColor || TEXT_COLOR_BLACK,
  );
  const [buttonBGColor, setButtonBGColor] = useState<string | undefined>(
    record?.buttonBackgroundColor || TEXT_COLOR_WHITE,
  );

  const [dataForm, setDataForm] = useState({});

  useEffect(() => {
    if (record) {
      if (record?.textColor) {
        setTextColor(record?.textColor);
      }
      if (record?.priceColor) {
        setPriceColor(record?.priceColor);
      }
      if (record?.buttonTextColor) {
        setButtonTextColor(record?.buttonTextColor);
      }
      if (record?.buttonBackgroundColor) {
        setButtonBGColor(record?.buttonBackgroundColor);
      }
      setDataForm(defaultValue);
    }
  }, [record]);

  const defaultValue = {
    title: record?.productName,
    ctaText: record?.buttonText,
    colorOpacity: record?.backgroundOpacity ? parseFloat(record?.backgroundOpacity) : DEFAULT_BACKGROUND_OPACITY,
    titleColor: record?.textColor || TEXT_COLOR_WHITE,
    priceColor: record?.priceColor || TEXT_COLOR_WHITE,
    ctaColor: record?.buttonTextColor || TEXT_COLOR_BLACK,
    textAlign: record?.buttonPosition || Align.LEFT,
    buttonBackgroundColor: record?.buttonBackgroundColor || TEXT_COLOR_WHITE,
    image: record?.image,
    maxQuantity: record?.maxQty || 1,
  };

  const onChangeInput = useCallback(
    e => {
      setDataForm(old => ({
        ...old,
        ...form.getFieldsValue(),
      }));
    },
    [form],
  );

  const updateProductInfo = async () => {
    try {
      const id = record?.product_id || '';
      setCreateLoading(true);
      const params: ProductUpdatable = {
        productName: form.getFieldValue('title'),
        maxQty: form.getFieldValue('maxQuantity'),
        priceColor,
        textColor,
        buttonText: form.getFieldValue('ctaText'),
        buttonPosition: form.getFieldValue('textAlign'),
        buttonTextColor,
        backgroundOpacity: form.getFieldValue('colorOpacity')?.toString() || '0',
        buttonBackgroundColor: buttonBGColor || TEXT_COLOR_WHITE,
        country: record?.country,
      };
      await updateProductRepository(id, params);
      onRefresh();
      onCloseModal();
      setDataRecord(null);
      setCreateLoading(false);
    } catch (error) {
      setCreateLoading(false);
      message.error(MESSAGE_ERROR.M02);
    }
  };

  const onFinish = async () => {
    Modal.confirm({
      title: t('popup.title'),
      okText: t('popup.okBtn'),
      cancelText: t('popup.cancelBtn'),
      icon: <ExclamationCircleOutlined />,
      content: (
        <ConfirmAction
          textConfirmAction={t('popup.update')}
          isConfirmPassword
          textConfirmPassword={t('popup.confirmPassword')}
        />
      ),
      onOk() {
        const inputPasswordNode = document.getElementById('inputPasswordForConfirm');
        const password = (inputPasswordNode as any).value;

        if (!userContext.user?.email || userContext.user?.email === '') {
          message.error(MESSAGE_ERROR.M02);
        } else {
          postLoginRepository({
            email: userContext.user?.email,
            password,
          })
            .then(() => {
              updateProductInfo();
            })
            .catch((e: any) => {
              message.error(e?.errorMessage || MESSAGE_ERROR.M02);
            });
        }
      },
    });
  };

  return (
    <>
      <SpinLoading isLoading={isCreateLoading} absolute>
        <SwitchContainer>
          <Form form={form} layout="vertical" initialValues={defaultValue} onFinish={onFinish} name="from-product">
            <Row className="form-product-container" gutter={[24, 24]}>
              <StyledSelectImageCol span={9}>
                <Preview
                  values={{
                    ...dataForm,
                    backgroundImage: record?.image,
                    textColor,
                    priceColor,
                    buttonBGColor,
                    buttonTextColor,
                  }}
                  isDisplayText
                  isToUpperCase
                  isPreview
                  height="293px"
                  isPromotionPreview
                />
              </StyledSelectImageCol>
              <StyledFormPromotion span={15}>
                <div className="form-scroll">
                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <Form.Item
                        name="title"
                        label={t('form.title')}
                        rules={[{ required: true, message: t('validate.titleBlank') }]}
                      >
                        <Input disabled placeholder={t('form.title')} maxLength={255} onChange={onChangeInput} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <Form.Item
                        name="maxQuantity"
                        label={t('form.maxQuantity')}
                        rules={[{ required: true, message: t('validate.maximumQuantity') }]}
                      >
                        <InputNumber
                          min={1}
                          max={10000}
                          defaultValue={form.getFieldValue('maxQuantity')}
                          onChange={onChangeInput}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]}>
                    <Col md={12} sm={24} xs={24} lg={12}>
                      <ColorPicker
                        title={t('form.titleColor')}
                        color={textColor}
                        onColorChanged={(value: string) => {
                          setTextColor(value);
                        }}
                      />
                    </Col>
                    <Col md={12} sm={24} xs={24} lg={12}>
                      <ColorPicker
                        title={t('form.priceColor')}
                        color={priceColor}
                        onColorChanged={(value: string) => {
                          setPriceColor(value);
                        }}
                      />
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <Form.Item name="ctaText" label={t('form.buttonText')}>
                        <Input placeholder={t('form.buttonText')} maxLength={255} onChange={onChangeInput} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]}>
                    <Col md={12} sm={24} xs={24} lg={12}>
                      <ColorPicker
                        title={t('form.buttonTextColor')}
                        color={buttonTextColor}
                        onColorChanged={(value: string) => {
                          setButtonTextColor(value);
                        }}
                      />
                    </Col>
                    <Col md={12} sm={24} xs={24} lg={12}>
                      <ColorPicker
                        title={t('form.buttonBGColor')}
                        color={buttonBGColor}
                        onColorChanged={(value: string) => {
                          setButtonBGColor(value);
                        }}
                      />
                    </Col>
                  </Row>

                  <Row gutter={[16, 16]}>
                    <Col md={12} sm={24} xs={24} lg={12}>
                      <Form.Item name="colorOpacity" label={t('form.colorOpacity')}>
                        <Slider min={0} max={1} step={0.01} onChange={onChangeInput} />
                      </Form.Item>
                    </Col>
                    <Col md={12} sm={24} xs={24} lg={12}>
                      <Form.Item name="textAlign" label={t('form.titleAlign')}>
                        <Radio.Group onChange={onChangeInput}>
                          <Radio value={Align.LEFT}>Left</Radio>
                          <Radio value={Align.CENTER}>Center</Radio>
                          <Radio value={Align.RIGHT}>Right</Radio>
                        </Radio.Group>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row style={{ justifyContent: 'flex-end', paddingTop: 20 }}>
                    <BtnContainer>
                      <ButtonActionPage
                        loading={isCreateLoading}
                        text={t('form.save')}
                        id="1"
                        className="btn-submit-form-home"
                        htmlType="submit"
                        icon={!record ? <FileAddOutlined /> : undefined}
                      />
                    </BtnContainer>
                  </Row>
                </div>
              </StyledFormPromotion>
            </Row>
          </Form>
        </SwitchContainer>
      </SpinLoading>
    </>
  );
};

const SwitchContainer = styled.div`
  & .ant-form-item-label {
    font-weight: bold;
    font-size: 16px;
  }

  & .form-promotion-container {
    padding: 1.5rem;

    .form-promotion__description {
      resize: none;
    }
  }

  .popover {
    position: absolute;
    z-index: 2;
    bottom: 0;
    left: 30%;
  }

  .cover {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }

  .sketch-picker {
    cursor: pointer;
  }

  & .ant-slider:hover .ant-slider-rail,
  & .ant-slider-rail {
    background-color: #6e6e6e;
  }
`;

const BtnContainer = styled.div`
  & .btn-submit-form-home {
    width: 160px;
  }
`;

const StyledSelectImageCol = styled(Col)`
  padding: 0px !important;
  min-height: 400px;
  height: 520px;
  background: transparent linear-gradient(180deg, #dedede00 0%, #7e7e7e 100%) 0% 0% no-repeat padding-box;
  position: relative;
  ${defaultImage}
`;

const StyledFormPromotion = styled(Col)`
  ${formScrolling}
  ${selectSelector}

  & input[type='text'],
  & input[type='number'] {
    ${appStyles.inputStyle}
  }

  & .ant-form-item-label {
    font-weight: bold;
    font-size: 16px;
  }

  & button.btn-submit-form-home {
    ${appStyles.commonButtonSubmitted}
  }

  & .ant-form-item {
    margin-bottom: 0.5rem;
  }

  & .ant-picker {
    width: 100%;
    padding: 0;
    ${appStyles.commonBorderRadius}
    & input {
      padding: 4px 0 4px 12px;
    }
    & .ant-picker-suffix {
      font-size: 20px;
      ${appStyles.background.black}
      ${appStyles.color.white}
      padding: 8px;
    }
  }

  & textarea {
    ${appStyles.commonBorderRadius}
  }

  & .ant-input-affix-wrapper {
    padding: 0 11px;
    ${appStyles.commonBorderRadius}
  }

  & .ant-form-item {
    ${appStyles.commonItemMarginBottom}
  }

  & .ant-form-item-explain {
    margin: 0;
  }

  & .ant-picker-clear {
    right: 40px;
  }

  & .ant-descriptions {
    cursor: default;
    & .ant-descriptions-header {
      margin-bottom: 5px;
    }
  }
`;
